import React, {useCallback, useEffect, useRef, useState} from 'react';
import {observer} from "mobx-react";
import {useStores} from "../stores/Context";
import Widget from "./Widgets/Widget";
import {Responsive, WidthProvider} from "react-grid-layout";
import {v4 as uuidv4} from "uuid";
import {GEN_DROP_ZONES} from '../utils/DragAndDrop';
import {useLongPress, LongPressDetectEvents} from "use-long-press";
import {useParams} from "react-router-dom";
import axios from "axios";
import html2canvas from 'html2canvas';
import BtnSvg from "../boiteAOutils/BtnSvg";
import {getDashboardThumbnailBounds} from "../utils/dashboardThumbnail";

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const NiveauFlash = observer(() => {
    const {ent} = useParams();
    const {uiStore, createWidgetStore, widgetStore, PlanningActionsStore} = useStores();
    const niveauFlashRef = useRef(null);
    const layoutCaptureRef = useRef(null);
    const [enabled, setEnabled] = useState(true);

    useEffect(() => {
        if (uiStore.isRedirected && widgetStore.widgetsLayout.length !== 0) {
            uiStore.isRedirected = false;
            uiStore.openPanel();
            ResizeGridLayout();
            widgetStore.updateAllWidgetStatic();
        }
    }, [widgetStore.widgetsLayout]);

    const captureScreenshot = useCallback(() => {
        const captureSurface = layoutCaptureRef.current;
        if (!captureSurface || !widgetStore.idTotem) {
            return;
        }

        const layout = captureSurface.querySelector('.react-grid-layout');
        if (!layout) return;

        const layoutRect = layout.getBoundingClientRect();
        const itemRects = Array.from(layout.querySelectorAll('.react-grid-item'))
            .map((item) => item.getBoundingClientRect());
        const bounds = getDashboardThumbnailBounds(layoutRect, itemRects);
        if (!bounds) return;

        html2canvas(layout, {
            useCORS: true,
            logging: false,
            scale: Math.min(window.devicePixelRatio || 1, 2),
            x: bounds.left,
            y: bounds.top,
            width: bounds.width,
            height: bounds.height,
            foreignObjectRendering: false,
            allowTaint: true,
            backgroundColor: "#F1F5F7",
            removeContainer: true,
            scrollX: 0,
            scrollY: 0,
            onclone: (documentClone) => {
                documentClone.querySelectorAll('.react-resizable-handle, .btnUpdateObjetElementaire, .btnRemoveObjetElementaire')
                    .forEach((element) => {
                        element.style.display = 'none';
                    });
            },
        }).then(canvas => {
            canvas.toBlob(blob => {
                if (!blob) return;
                const formData = new FormData();
                formData.append('image', blob, `${ent}.png`);
                formData.append('id_totem', widgetStore.idTotem);

                axios.post(`${process.env.REACT_APP_API_URL}/api/upload-miniature`, formData, {
                    headers: {"Authorization": `Bearer ${widgetStore.getCookie(`${ent}_apiToken`)}`}
                })
                    .then(() => {})
                    .catch(error => console.error('Error uploading screenshot:', error));
            }, 'image/png', 1.0);
            // En test -> Download l'image
            // canvas.toBlob(blob => {
            //     const link = document.createElement('a');
            //     link.href = URL.createObjectURL(blob);
            //     link.download = `${ent}.png`;
            //     link.click();
            // }, 'image/png', 1.0);
        }).catch(error => console.error('Erreur lors de la capture de la capture d\'écran :', error));
    }, [ent, widgetStore]);

    const dashboardLayoutSignature = widgetStore.widgetsLayout
        .map(({i, x, y, w, h}) => `${i}:${x}:${y}:${w}:${h}`)
        .join('|');

    useEffect(() => {
        if (!dashboardLayoutSignature || !widgetStore.idTotem) return undefined;

        // Les phidgets chargent leurs données de manière asynchrone. Laisser le
        // rendu se stabiliser évite d'enregistrer une miniature partiellement vide.
        const timer = window.setTimeout(captureScreenshot, 6000);
        return () => window.clearTimeout(timer);
    }, [captureScreenshot, dashboardLayoutSignature, widgetStore.idTotem]);

    const onDrop = (layout, layoutItem, _event) => {
        let data = _event.dataTransfer.getData("text/plain");
        try {
            data = JSON.parse(data);
            widgetStore.addWidget({
                i: uuidv4(),
                x: layoutItem.x,
                y: layoutItem.y,
                w: layoutItem.w,
                h: layoutItem.h,
                static: false,
                typeWidget: data.typeWidget,
                idWidget: data.idWidget
            }, ent);
        } catch (e) {
            console.error("Parsing error:", e);
        }
    };

    const debounceTimeoutRef = useRef(null);

    const onLayoutChange = (layout) => {
        widgetStore.widgetsLayout = layout.filter(i => i.i !== "__dropping-elem__");
        // Annuler le timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        // lance / relance le timeout
        debounceTimeoutRef.current = setTimeout(() => {
            SaveWidgetsPositions(false);
        }, 1000);
    };

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const SaveWidgetsPositions = (trigWebSocket = false) => {
        widgetStore.saveWidgetsPosition(trigWebSocket);
    }

    const removeWidget = (e, el) => {
        widgetStore.removeWidget(el);
    };

    const stopPropagation = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const callback = useCallback((e) => {
        e.preventDefault();
        stopPropagation(e);
    }, []);

    const bind = useLongPress(enabled ? callback : null, {
        onStart: () => console.log("Press started"),
        onFinish: () => console.log("Long press finished"),
        onCancel: () => console.log("Press cancelled"),
        threshold: 1000,
        captureEvent: true,
        cancelOnMovement: true,
        detect: LongPressDetectEvents.BOTH
    });

    const ResizeGridLayout = () => {
        for (let t = 0; t < 3000; t += 1000) {
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, t);
        }
    };

    return (
        <div
            className={`p-0 niveauflash ${uiStore.isPanelOpened ? uiStore.isPanelExtended ? 'sidebarActive extendPhidgets' : 'sidebarActive' : ''}`}
            ref={niveauFlashRef}
        >
            <div ref={layoutCaptureRef} className="dashboardThumbnailSurface">
            <ResponsiveReactGridLayout
                layouts={{lg: widgetStore.widgetsLayout}}
                layout={widgetStore.widgetsLayout}
                className="layout"
                onLayoutChange={onLayoutChange}
                breakpoints={{lg: 1080, md: 996, sm: 768, xs: 480, xxs: 0}}
                cols={{lg: 12, md: 12, sm: 12, xs: 12, xxs: 12}}
                margin={[10, 10]}
                containerPadding={[20, 20]}
                rowHeight={100}
                width={9}
                onDrop={onDrop}
                autoSize={false}
                transformScale={1}
                measureBeforeMount={false}
                compactType={null}
                preventCollision={true}
                isDroppable={true}
            >
                {widgetStore.widgetsLayout.map(el => {
                    const data = widgetStore.getWidgetData(el.i);
                    return (
                        <div key={el.i} data-grid={el}>
                            <Widget idWidget={data.idWidget} typeWidget={data.typeWidget}/>
                            {uiStore.isPanelOpened && (
                                uiStore.isDeleteModeWidget && (
                                    <BtnSvg svg={"croix"} height={50} width={50} onClick={e => removeWidget(e, el)}
                                            style={{
                                                position: "absolute",
                                                top: "0.5em",
                                                right: "0.7em",
                                                zIndex: 999999
                                            }}/>
                                )
                            )}
                        </div>
                    );
                })}
            </ResponsiveReactGridLayout>
            </div>
            <div className={`dropZone ${uiStore.isPanelExtended ? 'extendPhidgets' : ''}`}>
                {GEN_DROP_ZONES().map((zone, index) => (
                    <div key={index} className={"zone"}>{zone}</div>
                ))}
            </div>
        </div>
    );
});

export default NiveauFlash;
